import { useCallback, useEffect, useState } from "react";

import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { ulid } from "ulid";

import {
  DataGrid,
  gridRowSelectionIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid";

import { useOutletApi } from "@/features/authentication";
import { StatusEnum } from "@/features/goods-receipts";
import { formatDate, formatNumber, getErrorMessage } from "@/features/shared";
import {
  CustomAsyncAutocomplete,
  ProductVariantNameCell,
  useSnackbar,
} from "@/features/ui";

import { Button } from "@mui/material";

export const SelectGoodsReceiptProductVariantSection = () => {
  const { control } = useFormContext();
  const { showSnackbar } = useSnackbar();
  const apiRef = useGridApiRef();
  const [goodsReceipt, setGoodsReceipt] = useState(null);
  const [productVariants, setProductVariants] = useState([]);
  const [loadingProductVariants, setLoadingProductVariants] = useState(false);
  const [
    selectedGoodsReceiptProductVariants,
    setSelectedGoodsReceiptProductVariants,
  ] = useState([]);

  const { append } = useFieldArray({
    control,
    name: "purchaseInvoiceProductVariants",
  });

  const { append: appendAllocation, remove: removeAllocation } = useFieldArray({
    control,
    name: "purchaseDownPaymentAllocations",
  });

  const [
    seller,
    buyer,
    purchaseInvoiceProductVariants,
    purchaseDownPaymentAllocations,
  ] = useWatch({
    control,
    name: [
      "seller",
      "buyer",
      "purchaseInvoiceProductVariants",
      "purchaseDownPaymentAllocations",
    ],
    exact: true,
  });

  const outletApi = useOutletApi(buyer?.id);

  const disableBrowseGoodsReceipt = !seller;

  const fetchUnfinishedGoodsReceipt = useCallback(
    async (searchValue, { page, size } = {}) => {
      if (!seller || !buyer) return [];

      const unfinishedStatuses = [
        StatusEnum.RECEIVED,
        StatusEnum.PARTIALLY_INVOICED,
      ].join(",");

      const {
        data: { data },
      } = await outletApi.get("goods-receipts", {
        params: {
          status: unfinishedStatuses,
          sellerableId: seller?.id,
          keyword: searchValue || null,
          withPurchaseDownPayments: true,
          page,
          size,
        },
      });
      return data;
    },
    [buyer, seller, outletApi],
  );

  const fetchGoodsReceiptProductVariants = useCallback(
    async (referenceNumber) => {
      if (!referenceNumber || !buyer) return;

      setLoadingProductVariants(true);

      try {
        const {
          data: { data },
        } = await outletApi.get(
          `goods-receipts/${referenceNumber}/goods-receipt-product-variants`,
          {
            params: {
              excludeFullyInvoiced: true,
              withPurchaseOrderProductVariant: true,
              withPurchaseOrder: true,
            },
          },
        );

        // 🔥 Adjust remaining quantity based on GRPV
        const adjustedProductVariants = data
          .map((goodsReceiptProductVariant) => {
            // Calculate total quantity for this variant across all PIPV entries
            const totalPurchaseInvoiceQuantity = purchaseInvoiceProductVariants
              .filter(
                (purchaseInvoiceProductVariant) =>
                  purchaseInvoiceProductVariant.goodsReceiptProductVariantId ===
                  goodsReceiptProductVariant.id,
              )
              .reduce((total, pipv) => total + pipv.quantity, 0);

            return {
              ...goodsReceiptProductVariant,
              // Billable is received MINUS returned — the backend caps at the
              // same figure.
              adjustedRemainingQuantity:
                goodsReceiptProductVariant.quantity -
                (goodsReceiptProductVariant.returnedQuantity ?? 0) -
                goodsReceiptProductVariant.invoicedQuantity -
                totalPurchaseInvoiceQuantity, // Adjust based on total PIPV quantity
            };
          })
          .filter((variant) => variant.adjustedRemainingQuantity > 0); // 🚀 Keep only variants with available stock

        setProductVariants(adjustedProductVariants);
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingProductVariants(false);
      }
    },
    [buyer, purchaseInvoiceProductVariants, outletApi],
  );

  const fetchPurchaseDownPaymentsForGoodsReceipt = useCallback(
    async (referenceNumber) => {
      if (!referenceNumber || !buyer) return [];

      try {
        const {
          data: { data },
        } = await outletApi.get(
          `goods-receipts/${referenceNumber}/purchase-down-payments`,
        );
        return data;
      } catch (error) {
        showSnackbar(
          `Gagal memuat uang muka. ${getErrorMessage(error)}`,
        );
        return [];
      }
    },
    [buyer, showSnackbar, outletApi],
  );

  useEffect(() => {
    if (goodsReceipt) {
      fetchGoodsReceiptProductVariants(goodsReceipt.referenceNumber);
    } else {
      setProductVariants([]);
    }
    setSelectedGoodsReceiptProductVariants([]); // Reset selection when goods receipt changes
  }, [fetchGoodsReceiptProductVariants, goodsReceipt]);

  // Detach auto-attached allocations whose PO no longer appears in the
  // products list. Only prune newly-added rows so persisted allocations (edit
  // mode) stay until the user explicitly removes them.
  useEffect(() => {
    if (!purchaseDownPaymentAllocations.length) return;

    const involvedPurchaseOrderIds = new Set(
      purchaseInvoiceProductVariants
        .map((v) => v.purchaseOrderId)
        .filter(Boolean),
    );

    const indexesToRemove = [];
    purchaseDownPaymentAllocations.forEach((allocation, index) => {
      if (
        allocation.isNewlyAdded &&
        allocation.purchaseOrderId &&
        !involvedPurchaseOrderIds.has(allocation.purchaseOrderId)
      ) {
        indexesToRemove.push(index);
      }
    });

    if (indexesToRemove.length) {
      // Remove from highest index to lowest so earlier indexes stay valid.
      removeAllocation(indexesToRemove.reverse());
    }
  }, [
    purchaseInvoiceProductVariants,
    purchaseDownPaymentAllocations,
    removeAllocation,
  ]);

  const columns = [
    {
      field: "name",
      headerName: "Nama",
      flex: 5,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell name={name} sku={sku} />
      ),
    },
    {
      field: "adjustedRemainingQuantity",
      headerName: "Qty",
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "unitOfMeasurement",
      headerName: "Unit",
      width: 80,
      valueGetter: (_, row) => row.product.unitOfMeasurement.name,
    },
    {
      field: "note",
      headerName: "Catatan",
      width: 300,
      valueGetter: (value) => value || "-",
    },
  ];

  const submitSelection = async () => {
    const selectedRows = selectedGoodsReceiptProductVariants;
    const pickedGoodsReceipt = goodsReceipt;

    // Reset the state after submission
    setGoodsReceipt(null);
    setProductVariants([]);

    selectedRows.forEach(
      ({
        id: goodsReceiptProductVariantId,
        name,
        sku,
        note,
        adjustedRemainingQuantity,
        product: {
          unitOfMeasurement: { name: unitOfMeasurement },
        },
        purchaseOrder,
        purchaseOrderProductVariant,
      }) => {
        // Check if this GRPV is already in the invoice form
        const alreadyExists = purchaseInvoiceProductVariants.some(
          (piv) =>
            piv.goodsReceiptProductVariantId === goodsReceiptProductVariantId,
        );

        if (!alreadyExists) {
          append({
            id: ulid(),
            isNewlyAdded: true,
            goodsReceiptProductVariantId,
            purchaseOrderId:
              purchaseOrder?.id ?? purchaseOrderProductVariant?.purchaseOrderId,
            name,
            sku,
            quantity: adjustedRemainingQuantity,
            unitOfMeasurement,
            price: purchaseOrderProductVariant?.costPerUnit ?? 0,
            discount: 0,
            // Carried from the receipt line; still editable in the grid.
            note: note ?? null,
            returnedQuantity: 0,
          });
        }
      },
    );

    // Auto-attach any PDPs behind the selected goods receipt that still have
    // an allocatable amount, unless already present in the allocations list.
    if (pickedGoodsReceipt?.referenceNumber) {
      const existingSourceIds = new Set(
        purchaseDownPaymentAllocations.map(
          (allocation) => allocation.sourcePurchaseInvoiceId,
        ),
      );

      const purchaseDownPayments =
        await fetchPurchaseDownPaymentsForGoodsReceipt(
          pickedGoodsReceipt.referenceNumber,
        );

      purchaseDownPayments.forEach((purchaseDownPayment) => {
        // Allocatable is grand total minus what other invoices already took and
        // whatever was refunded — not outstandingAmount, which only tracks
        // settlement of the DP itself.
        const allocatedAmount = (
          purchaseDownPayment.purchaseDownPaymentAllocations ?? []
        ).reduce(
          (total, allocation) => total + Number(allocation.amount ?? 0),
          0,
        );
        const availableAmount =
          Number(purchaseDownPayment.grandTotal ?? 0) -
          allocatedAmount -
          Number(purchaseDownPayment.returnedAmount ?? 0);
        if (availableAmount <= 0) return;
        if (existingSourceIds.has(purchaseDownPayment.id)) return;
        existingSourceIds.add(purchaseDownPayment.id);

        appendAllocation({
          id: ulid(),
          isNewlyAdded: true,
          sourcePurchaseInvoiceId: purchaseDownPayment.id,
          referenceNumber: purchaseDownPayment.referenceNumber,
          // Keep the PO link so the pruning effect can detach this allocation
          // if its goods-receipt items are later removed from the invoice.
          purchaseOrderId: purchaseDownPayment.purchaseOrderId,
          availableAmount,
          amount: availableAmount,
          note: purchaseDownPayment.note ?? null,
        });
      });
    }
  };

  return (
    <>
      <CustomAsyncAutocomplete
        value={goodsReceipt}
        onChange={setGoodsReceipt}
        disabled={disableBrowseGoodsReceipt}
        fetchOptions={fetchUnfinishedGoodsReceipt}
        getOptionLabel={(option) => option.referenceNumber}
        isOptionEqualToValue={(option, selectedOption) =>
          option.id === selectedOption.id
        }
        renderOption={(props, option) => {
          const { id, referenceNumber, transactionDate } = option;
          return (
            <li {...props} key={id}>
              <div>
                <div>{referenceNumber}</div>
                <div style={{ fontSize: "0.75rem" }}>
                  {formatDate(transactionDate)}
                </div>
              </div>
            </li>
          );
        }}
        TextFieldProps={{
          helperText: disableBrowseGoodsReceipt
            ? "Pilih Pemasok terlebih dahulu"
            : undefined,
          InputProps: {
            placeholder: "Cari nomor referensi penerimaan barang",
          },
        }}
      />
      <DataGrid
        getRowHeight={() => "auto"}
        apiRef={apiRef}
        columns={columns}
        rows={productVariants}
        loading={loadingProductVariants}
        checkboxSelection
        onRowSelectionModelChange={() => {
          const selectedModels = Array.from(
            gridRowSelectionIdsSelector(apiRef.current.state).values(),
          );
          setSelectedGoodsReceiptProductVariants(selectedModels);
        }}
      />
      <Button
        fullWidth
        variant="contained"
        disabled={selectedGoodsReceiptProductVariants.length === 0}
        sx={{ mt: 1 }}
        onClick={submitSelection}
      >
        Tambah
      </Button>
    </>
  );
};
